import { useEffect, useRef, useState } from 'react';
import styles from './third-party-user-toaster.module.scss';
import { WarningIcon } from 'src/assets/svg/ute-notifier-icons';
import { InfoIconNavyBlue } from 'src/assets/svg/info-icon';

interface IThirdPartyUserToasterProps {
	clientName: string;
	activeSessionTimeInMinutes: number;
	showThirdPartySessionExpiryPopup: boolean;
	setShowThirdPartySessionExpiryPopup: (showThirdPartySessionExpiryPopup: boolean) => void;
	logout: () => void;
}

const ThirdPartyUserToaster: React.FC<IThirdPartyUserToasterProps> = ({
	clientName,
	activeSessionTimeInMinutes,
	showThirdPartySessionExpiryPopup,
	setShowThirdPartySessionExpiryPopup,
	logout,
}) => {
	const previousTimeLeft = useRef<number>();
	const [timeLeft, setTimeLeft] = useState<number>(0);
	const isLessThan15Minutes = timeLeft <= 15 * 60 * 1000;

	useEffect(() => {
		const sessionDurationMs = activeSessionTimeInMinutes * 60 * 1000;
		const storedEndTime = localStorage.getItem('sessionEndTime');
		const currentTime = Date.now();
		let endTime: number;

		if (storedEndTime) {
			endTime = parseInt(storedEndTime, 10);
			if (currentTime > endTime || isNaN(endTime)) {
				endTime = currentTime + sessionDurationMs;
				localStorage.setItem('sessionEndTime', endTime.toString());
			}
		} else {
			endTime = currentTime + sessionDurationMs;
			localStorage.setItem('sessionEndTime', endTime.toString());
		}

		const calculateTimeLeft = () => {
			const now = Date.now();
			const remaining = endTime - now;
			return remaining > 10 ? remaining : 0;
		};

		setTimeLeft(calculateTimeLeft());

		const timerId = setInterval(() => {
			const newTimeLeft = calculateTimeLeft();
			setTimeLeft(newTimeLeft);

			if (
				previousTimeLeft.current !== undefined &&
				previousTimeLeft.current > 15 * 60 * 1000 &&
				newTimeLeft <= 15 * 60 * 1000 &&
				!showThirdPartySessionExpiryPopup
			) {
				setShowThirdPartySessionExpiryPopup(true);
			}
			previousTimeLeft.current = newTimeLeft;

			if (previousTimeLeft.current !== undefined && previousTimeLeft.current > 10 && newTimeLeft <= 1 * 1000) {
				if (showThirdPartySessionExpiryPopup) {
					setShowThirdPartySessionExpiryPopup(false);
				}
				clearInterval(timerId);
				logout();
			}
		}, 1000);

		// Fallback to handle inactive tabs
		const visibilityChangeHandler = () => {
			if (document.visibilityState === 'visible') {
				const newTimeLeft = calculateTimeLeft();
				setTimeLeft(newTimeLeft);

				if (newTimeLeft <= 0) {
					if (showThirdPartySessionExpiryPopup) {
						setShowThirdPartySessionExpiryPopup(false);
					}
					clearInterval(timerId);
					logout();
				}
			}
		};

		document.addEventListener('visibilitychange', visibilityChangeHandler);

		return () => {
			clearInterval(timerId);
			document.removeEventListener('visibilitychange', visibilityChangeHandler);
		};
	}, [activeSessionTimeInMinutes, showThirdPartySessionExpiryPopup]);

	const formatTime = (milliseconds: number): string => {
		const totalSeconds = Math.floor(milliseconds / 1000);
		const hours = Math.floor(totalSeconds / 3600);
		const minutes = Math.floor((totalSeconds % 3600) / 60);
		const seconds = totalSeconds % 60;

		if (hours > 0) {
			return `${String(hours)}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
		}
		return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
	};

	return (
		<div className={`${styles.container} ${isLessThan15Minutes ? styles.warning : styles.info}`}>
			{isLessThan15Minutes ? <WarningIcon /> : <InfoIconNavyBlue />}
			<div className={styles.content}>
				<p>
					You're currently in <span className={styles.bold}>{clientName}'s</span> Client Portal. Your session will
					expire in <span className={styles.bold}>{formatTime(timeLeft)}</span>
				</p>
			</div>
		</div>
	);
};

export default ThirdPartyUserToaster;
